import React, { useState, useEffect, useMemo } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { CopyIconSuccess, CopyIcon, QuoteIcon, RetryIcon, CopyIconSuccessCircle } from "@assets/icon";
import { formatChatTime } from "@utils/index";
import type { ChatMessage } from "../../../../types/chat-agent";
import { ChatAgentType } from "@constants/chat-agents";
import { SenderType } from "../../../../types/chat-message";
import {
  extractImageUrlsFromBlockContents,
  extractTextFromTextBlocksOnly,
  isImagesBlockType,
} from "@utils/chat-message";

import ImageGallery from "./ImageGallery";
import { getComponentChipLabel } from "../../../../types/chat-component";
import "./ChatUser.scss";
import { useTranslation } from "react-i18next";

type ChatUserProps = {
  message: string;
  time?: string;
  isRead?: boolean;
  messageId?: string;
  uploadStatus?: 'uploading' | 'success' | 'failed';
  uploadError?: string;
  fullMessage?: ChatMessage; // Full message object for retry
  onRetryUpload?: (message: ChatMessage) => void;
  onQuote?: (message: ChatMessage) => void; // Quote/Reply handler
  activeAgent?: ChatAgentType; // To check if in CSKH
  isQueuing?: boolean; // Flag for messages waiting for connection
  scrollToQuotedMessage?: (targetMessageId: string | number) => Promise<void>;
};

const cleanMessageText = (text: string) =>
  text.replace(/<!--[\s\S]*?-->/g, '').trim();

const isMarkdownImagesOnly = (text: string) => {
  const cleaned = cleanMessageText(text);
  if (!cleaned) return false;
  return Boolean(cleaned) && /^(\!\[image\]\(.*?\)(\s|\\n|\\r\\n)*)+$/.test(cleaned);
};

const ChatUser: React.FC<ChatUserProps> = ({
  message,
  time,
  uploadStatus,
  fullMessage,
  onRetryUpload,
  onQuote,
  activeAgent,
  scrollToQuotedMessage,
}) => {
  const { t } = useTranslation();
  const formattedTime = formatChatTime(time);
  const [showCopyToast, setShowCopyToast] = useState(false);

  // Auto hide toast after 3 seconds
  useEffect(() => {
    if (showCopyToast) {
      const timer = setTimeout(() => {
        setShowCopyToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showCopyToast]);

  const handleCopySuccess = () => {
    setShowCopyToast(true);
  };

  const handleRetryClick = () => {
    if (fullMessage && onRetryUpload) {
      onRetryUpload(fullMessage);
    }
  };

  const handleQuoteClick = () => {
    if (fullMessage && onQuote) {
      onQuote(fullMessage);
    }
  };

  // Extract image URLs from markdown message for gallery display
  const { imageUrls, textWithoutImages } = useMemo(() => {
    if (!message) return { imageUrls: [], textWithoutImages: '' };
    // Strip HTML comments
    const cleaned = message.replace(/<!--[\s\S]*?-->/g, '').trim();
    // Extract ![image](url) patterns
    const imgRegex = /!\[image\]\((.*?)\)/g;
    const urls: string[] = [];
    let match;
    while ((match = imgRegex.exec(cleaned)) !== null) {
      urls.push(match[1]);
    }
    // Remove image markdown from text
    const remaining = cleaned.replace(/!\[image\]\(.*?\)/g, '').trim();
    return { imageUrls: urls, textWithoutImages: remaining };
  }, [message]);

  const imageUploadItems = fullMessage?.imageUploadItems;
  const hasImageUploadItems = Boolean(imageUploadItems && imageUploadItems.length > 0);

  const hasImagesBlock = useMemo(
    () => Boolean(fullMessage?.blockContents?.some((b) => isImagesBlockType(b))),
    [fullMessage?.blockContents],
  );

  const textFromTextBlocksOnly = useMemo(
    () => extractTextFromTextBlocksOnly(fullMessage?.blockContents),
    [fullMessage?.blockContents],
  );

  const blockImageUrls = useMemo(
    () => extractImageUrlsFromBlockContents(fullMessage?.blockContents),
    [fullMessage?.blockContents],
  );

  const rowGalleryUrls = useMemo(() => {
    if (!hasImagesBlock || hasImageUploadItems) return [];
    return blockImageUrls.length > 0 ? blockImageUrls : imageUrls;
  }, [hasImagesBlock, hasImageUploadItems, blockImageUrls, imageUrls]);

  const useApiImagesRowLayout =
    hasImagesBlock && !hasImageUploadItems && rowGalleryUrls.length > 0;

  const bubbleMarkdownForApiRow = useMemo(() => {
    const t = textFromTextBlocksOnly.trim();
    return t || textWithoutImages.trim();
  }, [textFromTextBlocksOnly, textWithoutImages]);

  const galleryImageUrls = hasImageUploadItems
    ? imageUploadItems!.map((item) => item.s3Url || item.localUrl)
    : imageUrls;

  const showGalleryAbove =
    (useApiImagesRowLayout && rowGalleryUrls.length > 0) ||
    (!useApiImagesRowLayout &&
      (galleryImageUrls.length > 0 || hasImageUploadItems));

  const isImageOnly = useMemo(() => {
    if (fullMessage?.attachedComponent && !message?.trim()) return false;
    if (fullMessage?.text && isMarkdownImagesOnly(fullMessage.text)) return true;
    if (useApiImagesRowLayout) {
      return !bubbleMarkdownForApiRow.trim() && rowGalleryUrls.length > 0;
    }
    // if (!message?.trim()) return false;
    if (fullMessage?.uploadStatus) return true;
    if (fullMessage?.imageUploadItems && fullMessage.imageUploadItems.length > 0 && !message?.trim()) return true;
    if (isMarkdownImagesOnly(message)) return true;

    const cleaned = cleanMessageText(message);
    if (!cleaned) return false;
    if (/^(https?:\/\/\S+\.(?:png|jpe?g|gif|webp)(\?\S*)?\s*)+$/i.test(cleaned)) return true;
    return false;
  }, [
    useApiImagesRowLayout,
    bubbleMarkdownForApiRow,
    rowGalleryUrls.length,
    message,
    fullMessage?.uploadStatus,
    fullMessage?.imageUploadItems,
    fullMessage?.text,
    fullMessage?.attachedComponent,
  ]);

  const showQuoteButton = activeAgent === ChatAgentType.CSKH && !isImageOnly;
  
  

  // Check if any image has failed (for showing retry button)
  const hasFailedImages = hasImageUploadItems
    ? imageUploadItems!.some(item => item.status === 'failed')
    : uploadStatus === 'failed';

  // Get quoted message label if this message is a reply
  const getQuotedLabel = () => {
    // Case 1: Local quote (set via UI quote action)
    if (fullMessage?.quotedMessageId && fullMessage?.quotedMessageUser) {
      const quotedUser = fullMessage.quotedMessageUser;
      if (quotedUser === SenderType.USER) {
        return 'Bạn đã trả lời bạn';
      } else if (activeAgent === ChatAgentType.CSKH) {
        return 'Bạn đã trả lời CSKH';
      } else {
        return 'Bạn đã trả lời Tendoo AI';
      }
    }

    // Case 2: From backend history (replyToMessageId)
    if (fullMessage?.replyToMessageId) {
      const replyToMessage = fullMessage.replyToMessage;
      if (replyToMessage) {
        const replyType = replyToMessage.senderType;
        if (replyType === 'USER' || replyType === SenderType.USER) {
          return 'Bạn đã trả lời bạn';
        }
        return 'Bạn đã trả lời CSKH';
      }
      return 'Bạn đã trả lời';
    }

    return null;
  };

  const stripMarkdown = (md: string) => {
    if (!md) return '';

    return md
      .replace(/!\[image\]\([^)]*\)/g, '')
      .replace(/^#+\s+/gm, '')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/^[ \t]*[*\-+]\s+/gm, '')
      .replace(/^[ \t]*\d+\.\s+/gm, '')
      .replace(/\*([^*\n]+)\*/g, '$1')
      .replace(/_([^_\n]+)_/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/```[\s\S]*?```/g, '')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/^\s*>/gm, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  // Get quoted preview text (clean, 1 line)
  const getQuotedPreview = () => {
    // Case 1: Local quote text
    if (fullMessage?.quotedMessageText) {
      let text = fullMessage.quotedMessageText;

      // Check if image-only
      const hasOnlyImages = text.trim().match(/^(\!\[image\]\(.*?\)\s*)+$/);
      if (hasOnlyImages) return 'Hình ảnh';

      if(fullMessage.quotedMessageUser === SenderType.BOT) {
        return stripMarkdown(text);
      }

      // Remove images and strip markdown
      text = text
        .replace(/\!\[image\]\(.*?\)/g, '')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/__([^_]+)__/g, '$1')
        .replace(/_([^_]+)_/g, '$1')
        .replace(/\n/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      return text || 'Hình ảnh';
    }

    // Case 2: From backend history (replyToMessage object)
    if (fullMessage?.replyToMessage) {
      const replyMsg = fullMessage.replyToMessage;

      if(replyMsg.senderType === SenderType.BOT) {
        return stripMarkdown(replyMsg.content);
      }

      // Primary: use content field (backend returns { senderType, content, replyToTypeMessage })
      if (replyMsg.content) {
        return replyMsg.content.replace(/\n/g, ' ').trim();
      }
      // Fallback: try to extract text from blockContents
      if (replyMsg.blockContents?.length) {
        const textBlock = replyMsg.blockContents.find(
          (b: any) => b.type === 'TEXT' && b.payload?.text?.trim()
        );
        if (textBlock) {
          return (textBlock.payload as any).text.replace(/\n/g, ' ').trim();
        }
      }
      if (replyMsg.fullContent) {
        return replyMsg.fullContent.replace(/\n/g, ' ').trim();
      }
      return '';
    }

    return '';
  };

  return (
    <React.Fragment>
      {showCopyToast && (
        <div className="copy-toast ">
          <div className="copy-toast-content">
            <CopyIconSuccessCircle/>
            {t('common.copySuccess')}
          </div>
        </div>
      )}
      <div className="chat-user flex justify-end mt-3">
        {/* Quoted Label - Outside bubble */}
        {getQuotedLabel() && (
          <div className="quoted-label flex gap-2 items-center text-[12px] text-[var(--colour-neutral-base)] text-right mb-1">
            <QuoteIcon /> {getQuotedLabel()}
          </div>
        )}
      </div>

      {getQuotedLabel() && (
        <div className="flex justify-end">
          <button
            className={`!bg-[#F8F8F9] flex items-end max-w-[75%] text-left rounded-2xl rounded-br-none ${typeof getQuotedPreview() === "string" ? "p-[12px]" : ""} gap-[10px]`}
            style={{ cursor: 'pointer', border: 'none', background: 'rgba(255,255,255,0.9)', outline: 'none' }}
            onClick={() => {
              const targetId = fullMessage?.quotedMessageId || fullMessage?.replyToMessageIdStr
              if (targetId) {
                if (scrollToQuotedMessage) {
                  scrollToQuotedMessage(targetId);
                } else {
                  const element =
                    document.getElementById(`message-${targetId}`) ||
                    document.querySelector(`[data-message-id="${targetId}"]`) ||
                    document.querySelector(`[data-id="${targetId}"]`);

                  if (element instanceof HTMLElement) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }
              }
            }}
          >
            <div className="text-[14px] text-[var(--colour-neutral-base)] truncate">
              {getQuotedPreview()}
            </div>
          </button>
        </div>
      )}

      <div className="chat-user flex justify-end">
        {hasFailedImages && (
          <div className="flex flex-col justify-center mb-2 mr-2">
            <button
              onClick={handleRetryClick}
              className="text-gray-500 hover:opacity-70 transition-colors"
              title="Thử lại"
            >
              <RetryIcon style={{ width: 24, height: 24, cursor: 'pointer' }} />
            </button>
          </div>
        )}

        {/* Quoted Message Display - Inside bubble with red border */}
        <div className="flex flex-col items-end max-w-[85%]">
          {showGalleryAbove && (
            <div className="chat-user__gallery-row mb-2 w-full flex justify-end">
              <ImageGallery
                imageUrls={useApiImagesRowLayout ? rowGalleryUrls : galleryImageUrls}
                imageUploadItems={hasImageUploadItems ? imageUploadItems : undefined}
              />
            </div>
          )}

          {
            (!isImageOnly || fullMessage?.attachedComponent) && (
            <div className="border-chat-bot break-words p-3">
              {fullMessage?.attachedComponent && (
                <div className="mb-2 max-w-full truncate rounded-lg bg-[rgba(61,61,67,0.06)] px-2.5 py-1.5 text-[13px] leading-[18px] text-[#242428]">
                  {getComponentChipLabel(fullMessage.attachedComponent)}
                </div>
              )}
              {useApiImagesRowLayout ? (
                bubbleMarkdownForApiRow ? (
                  <ReactMarkdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ inline, className, children, ...props }: any) {
                        const match = /language-(\w+)/.exec(className || "");
                        return !inline && match ? (
                          <SyntaxHighlighter
                            {...props}
                            style={materialDark}
                            language={match[1]}
                            PreTag="div"
                          >
                            {String(children).replace(/\n$/, "")}
                          </SyntaxHighlighter>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      },
                    }}
                  >
                    {bubbleMarkdownForApiRow}
                  </ReactMarkdown>
                ) : null
              ) : galleryImageUrls.length > 0 || hasImageUploadItems ? (
                textWithoutImages ? (
                  <ReactMarkdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ inline, className, children, ...props }: any) {
                        const match = /language-(\w+)/.exec(className || "");
                        return !inline && match ? (
                          <SyntaxHighlighter
                            {...props}
                            style={materialDark}
                            language={match[1]}
                            PreTag="div"
                          >
                            {String(children).replace(/\n$/, "")}
                          </SyntaxHighlighter>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      },
                    }}
                  >
                    {textWithoutImages}
                  </ReactMarkdown>
                ) : null
              ) : (
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  remarkPlugins={[remarkGfm]}
                  components={{
                    code({ inline, className, children, ...props }: any) {
                      const match = /language-(\w+)/.exec(className || "");
                      return !inline && match ? (
                        <SyntaxHighlighter
                          {...props}
                          style={materialDark}
                          language={match[1]}
                          PreTag="div"
                        >
                          {String(children).replace(/\n$/, "")}
                        </SyntaxHighlighter>
                      ) : (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    },
                  }}
                >
                  {message}
                </ReactMarkdown>
              )}

              <div className="chat-user-actions flex items-center justify-end gap-2 ml-[10px] mt-[4px] font-size-time">
                {/* Reply Button - Only in CSKH */}
                {showQuoteButton && (
                  <button
                    className="chat-action-btn"
                    aria-label="Trích dẫn"
                    type="button"
                    data-tooltip-id="chatbot-tooltip"
                    data-tooltip-content="Trích dẫn"
                    onClick={handleQuoteClick}
                  >
                    <QuoteIcon />
                  </button>
                )}
                {
                  !isImageOnly && (
                    <CopyToClipboard text={stripMarkdown(message)} onCopy={handleCopySuccess}>
                      <button
                        className="chat-action-btn chat-action-btn-copy"
                        aria-label="Sao chép"
                        type="button"
                        data-tooltip-id="chatbot-tooltip"
                        data-tooltip-content="Sao chép"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        {showCopyToast ? (
                          <CopyIconSuccess />
                        ) : (
                          <CopyIcon />
                        )}
                      </button>
                    </CopyToClipboard>
                  )
                }
                {formattedTime && <div className="chat-time">{formattedTime}</div>}
              </div>
            </div>
            )
          }

          {/* Legacy upload status for non-per-image messages - placed outside the bubble */}
          {!hasImageUploadItems && uploadStatus === 'uploading' && (
            <div className="mt-1 text-[12px] text-gray-500 flex items-center gap-1 justify-end">
              <span>Đang tải lên...</span>
            </div>
          )}
          {!hasImageUploadItems && uploadStatus === 'failed' && (
            <button
              className="flex items-center gap-1 mt-1 justify-end cursor-pointer"
              style={{ border: 'none', background: 'none', padding: 0 }}
              onClick={handleRetryClick}
              title="Thử lại"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="#E54646" />
              </svg>
              <span className="text-[12px] font-medium text-[#E54646]">Chưa thể gửi</span>
            </button>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default React.memo(ChatUser, (prevProps, nextProps) => {
  return (
    prevProps.messageId === nextProps.messageId &&
    prevProps.message === nextProps.message &&
    prevProps.isRead === nextProps.isRead &&
    prevProps.time === nextProps.time &&
    prevProps.uploadStatus === nextProps.uploadStatus &&
    prevProps.uploadError === nextProps.uploadError &&
    prevProps.isQueuing === nextProps.isQueuing &&
    prevProps.fullMessage === nextProps.fullMessage &&
    prevProps.activeAgent === nextProps.activeAgent
  );
});
